import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Image from '@atlaskit/image';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { Ati } from '@atlassian/ari';

import { CONTENT_CREATION_PAYWALL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils/entry-points/useIsAllowedToCreateWhiteboard.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { fg } from '@confluence/feature-gating';
import { isExperimentEnabled } from '@confluence/feature-experiments';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { useUniversalCreateState } from '../useUniversalCreate.ts';
import type { WhiteboardCreationLinkOptions } from '../../CreateContentRouteComponent/index.ts';
import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';
import {
	collectStartCreateWhiteboardExperience,
	useWhiteboardCreationPaywall,
} from '../../CreateContentPopover/CreateContentPopoverItems/useWhiteboardCreationPaywall.ts';
import type { WhiteboardCreationPaywallDialogProps } from '../../CreateContentPopover/CreateContentPopoverItems/WhiteboardCreationPaywallDialog.tsx';
import { WhiteboardCreationPaywallDialog } from '../../CreateContentPopover/CreateContentPopoverItems/WhiteboardCreationPaywallDialog.tsx';
import { PaywallStatus } from '../../createContentInputTypes.ts';
import RacecarIcon from '../../assets/Racecar.svg';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.whiteboard.title',
		defaultMessage: 'Whiteboard',
		description: 'title indicating that this option will create a whiteboard',
	},
	toolTipTextAnonymous: {
		id: 'create-blank-fabric-page.universal-create.whiteboard.tooltip.text-anonymous',
		defaultMessage: 'Log in to create a whiteboard',
		description: 'tooltip message telling user to sign in to create a whiteboard',
	},
	toolTipTextUnlicensed: {
		id: 'create-blank-fabric-page.universal-create.whiteboard.tooltip.text-unlicensed',
		defaultMessage: 'Sign up to Confluence to create a whiteboard',
		description: 'tooltip message telling user to sign up for Confluence to create a whiteboard',
	},
});

const PAYWALL_EXPERIENCE_ATTRIBUTES = {
	contentType: 'whiteboard',
	paywallFlow: 'upfront',
};

const f1GameStyles = cssMap({
	// Beep Beep! - the F1 Game Wrapper needs some liberal custom styling to correctly style the race car on hover without
	// causing frequent re-mounting of the (much more important) actual buttons in the menu
	itemWrapper: {
		alignItems: 'center',
		justifyContent: 'space-between',
		display: 'flex',
		position: 'relative',
	},
	childrenWrapper: {
		flexGrow: '1',
	},
	rightIcon: {
		height: '14px',
		position: 'absolute',
		right: token('space.200'),
		pointerEvents: 'none',
	},
});

const F1GameWrapper = ({ children }) => {
	const [showRacecarIcon, setShowRacecarIcon] = useState<boolean>(false);
	const handleOnMouseEnter = (_: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => {
		isExperimentEnabled('confluence_race_mode') && setShowRacecarIcon(true);
	};
	const handleOnMouseLeave = (_: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => {
		isExperimentEnabled('confluence_race_mode') && setShowRacecarIcon(false);
	};

	return (
		<div
			css={f1GameStyles.itemWrapper}
			onMouseEnter={handleOnMouseEnter}
			onFocus={handleOnMouseEnter}
			onMouseLeave={handleOnMouseLeave}
			onBlur={handleOnMouseLeave}
		>
			<div css={f1GameStyles.childrenWrapper}>{children()}</div>
			{showRacecarIcon && isExperimentEnabled('confluence_race_mode') && (
				// Skrrt! - The racecar icon is added in the wrapper instead of in the children component to prevent re-rendering/mounting issues
				<Image
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- Zoooom! Necessary to prevent inheritence from higher level css specificity
					css={f1GameStyles.rightIcon}
					src={RacecarIcon}
					srcDark={RacecarIcon}
					alt=""
					testId="confluence-whiteboards-car-icon"
				/>
			)}
		</div>
	);
};

export function useWhiteboardItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { isOpen } = useUniversalCreateState();
	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();
	// const { accessStatus } = useSessionData();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);

	// https://switcheroo.atlassian.com/ui/gates/43f5fd03-3bd9-48a0-b583-5de8b778977a/key/load-whiteboards-paywall-query-when-menu-is-open
	const skipPaywallQuery = fg('load-whiteboards-paywall-query-when-menu-is-open') && !isOpen;
	const {
		deactivationIdentifier,
		error: creationPaywallError,
		paywallStatus,
		showPaywallBeforeCreate,
	} = useWhiteboardCreationPaywall(skipPaywallQuery);
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-whiteboard-popover-item',
	});

	const buildWhiteboardCreationLink = (
		creationOverrides: Partial<WhiteboardCreationLinkOptions> = {},
		sourceSuffix = '',
	) => {
		return buildCreationLink({
			contentType: 'whiteboard',
			parentPageId: parentId,
			withFallback: true,
			source: source + sourceSuffix,
			spaceKey,
			deactivationIdentifier,
			paywallStatus,
			...creationOverrides,
		});
	};

	const createHref = buildWhiteboardCreationLink();

	const hybridLinkClick = useHybridLinkClick(createHref);

	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createWhiteboardPopoverItem');

		// https://switcheroo.atlassian.com/ui/gates/4fe23e91-1be4-4713-93b6-17b6bc84e9da/key/confluence_content_creation_paywall_fix_enabled
		if (fg('confluence_content_creation_paywall_fix_enabled')) {
			experienceTracker.start({
				name: CONTENT_CREATION_PAYWALL_EXPERIENCE,
				attributes: PAYWALL_EXPERIENCE_ATTRIBUTES,
				collect: collectStartCreateWhiteboardExperience,
			});
			if (creationPaywallError) {
				experienceTracker.fail({
					name: CONTENT_CREATION_PAYWALL_EXPERIENCE,
					error: creationPaywallError,
				});
			}
		}
		if (showPaywallBeforeCreate) {
			// stop the default behaviour - we want to show our modal instead
			e.stopPropagation();
			e.preventDefault();

			showModal<WhiteboardCreationPaywallDialogProps>(WhiteboardCreationPaywallDialog, {
				buildWhiteboardCreationLink,
			});
		} else {
			if (paywallStatus === PaywallStatus.UNSET) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'clicked',
						actionSubject: 'createUnsetWhiteboardLink',
						source,
						attributes: {
							reason: creationPaywallError ? 'error' : 'loading',
						},
					},
				}).fire();
			}
			hybridLinkClick(e);
		}
	};

	// TODO: add tooltip back post T25
	// const getTooltipContent = (accessStatusArg: AccessStatus | null): string | undefined => {
	// 	if (!isAllowedToCreateWhiteboard) {
	// 		if (accessStatusArg === null) {
	// 			return formatMessage(messages.toolTipTextAnonymous);
	// 		} else {
	// 			return formatMessage(messages.toolTipTextUnlicensed);
	// 		}
	// 	}
	// 	return undefined;
	// };

	// const toolTipContent = getTooltipContent(accessStatus);

	return {
		ati: Ati.parse('ati:cloud:confluence:whiteboard'),
		children: (
			<div>
				{/* https://switcheroo.atlassian.com/ui/gates/4fe23e91-1be4-4713-93b6-17b6bc84e9da/key/confluence_content_creation_paywall_fix_enabled */}
				{!fg('confluence_content_creation_paywall_fix_enabled') && (
					<>
						<ExperienceStart
							name={CONTENT_CREATION_PAYWALL_EXPERIENCE}
							collect={collectStartCreateWhiteboardExperience}
							attributes={PAYWALL_EXPERIENCE_ATTRIBUTES}
						/>
						{creationPaywallError && (
							<ExperienceFailure
								name={CONTENT_CREATION_PAYWALL_EXPERIENCE}
								error={creationPaywallError}
							/>
						)}
					</>
				)}
				{formatMessage(messages.title)}
			</div>
		),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: WhiteboardIcon,
		...(isExperimentEnabled('confluence_race_mode') && {
			Wrapper: F1GameWrapper,
		}),
		...(!shouldBlockCreate && isAllowedToCreateWhiteboard && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
